import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../hooks/ProjectContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Checkbox,
  Alert,
} from '@mui/material';

const DEFAULT_ELIGIBILITY = {
  landType: 'unknown',
  croppingSystem: '',
  accessPermission: false,
  boundaryConfirmed: false,
  hasWaterBodies: 'unknown',
  hasCanalsRoads: 'unknown',
  hasProtectedZones: 'unknown',
  notes: '',
  decision: 'undecided',
  confidence: 'low',
  mainRisks: '',
  updatedAt: null,
};

export default function FeasibilityEligibilityPage() {
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const storageKey = selectedProject?.id
    ? `terrra.project.${selectedProject.id}.feasibility.eligibility.v0`
    : null;

  const initial = useMemo(() => {
    if (!storageKey) return DEFAULT_ELIGIBILITY;
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : DEFAULT_ELIGIBILITY;
    } catch (_) { return DEFAULT_ELIGIBILITY; }
  }, [storageKey]);

  const [form, setForm] = useState(initial);
  const [saved, setSaved] = useState(false);

  // Reset form when project changes
  useEffect(() => {
    setSaved(false);
    setForm(initial);
  }, [initial]);

  if (!selectedProject) {
    return (
      <>
        <Helmet><title>Feasibility Eligibility | TERRRA</title></Helmet>
        <Container maxWidth="xl">
          <Alert severity="info" sx={{ mt: 3 }}>Select a project from the header to continue.</Alert>
        </Container>
      </>
    );
  }

  function onSave() {
    const out = { ...form, updatedAt: new Date().toISOString() };
    if (storageKey) localStorage.setItem(storageKey, JSON.stringify(out));
    setForm(out);
    setSaved(true);
  }

  return (
    <>
      <Helmet>
        <title> Feasibility Eligibility | TERRRA </title>
      </Helmet>

      <Container maxWidth="md">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4">Feasibility — Eligibility</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Minimal v0 form (stored locally). This will be backed by the project Summary API next.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/project/home')}>
            Back to Project
          </Button>
        </Stack>

        {saved && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Saved (local). Updated at {new Date(form.updatedAt).toLocaleString()}.
          </Alert>
        )}

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              A. Basic checks
            </Typography>

            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="landTypeLabel">Land type</InputLabel>
                <Select
                  labelId="landTypeLabel"
                  label="Land type"
                  value={form.landType}
                  onChange={(e) => setForm((v) => ({ ...v, landType: e.target.value }))}
                >
                  <MenuItem value="unknown">Unknown</MenuItem>
                  <MenuItem value="agriculture">Agriculture</MenuItem>
                  <MenuItem value="pasture">Pasture</MenuItem>
                  <MenuItem value="forest">Forest</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Cropping system (optional)"
                value={form.croppingSystem}
                onChange={(e) => setForm((v) => ({ ...v, croppingSystem: e.target.value }))}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.accessPermission}
                      onChange={(e) => setForm((v) => ({ ...v, accessPermission: e.target.checked }))}
                    />
                  }
                  label="Access permission obtained"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.boundaryConfirmed}
                      onChange={(e) => setForm((v) => ({ ...v, boundaryConfirmed: e.target.checked }))}
                    />
                  }
                  label="Boundary confirmed"
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              B. Exclusions / constraints
            </Typography>

            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="waterLabel">Water bodies present?</InputLabel>
                  <Select
                    labelId="waterLabel"
                    label="Water bodies present?"
                    value={form.hasWaterBodies}
                    onChange={(e) => setForm((v) => ({ ...v, hasWaterBodies: e.target.value }))}
                  >
                    <MenuItem value="unknown">Unknown</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="canalsLabel">Canals/roads significant?</InputLabel>
                  <Select
                    labelId="canalsLabel"
                    label="Canals/roads significant?"
                    value={form.hasCanalsRoads}
                    onChange={(e) => setForm((v) => ({ ...v, hasCanalsRoads: e.target.value }))}
                  >
                    <MenuItem value="unknown">Unknown</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <FormControl fullWidth>
                <InputLabel id="protectedLabel">Protected zones?</InputLabel>
                <Select
                  labelId="protectedLabel"
                  label="Protected zones?"
                  value={form.hasProtectedZones}
                  onChange={(e) => setForm((v) => ({ ...v, hasProtectedZones: e.target.value }))}
                >
                  <MenuItem value="unknown">Unknown</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm((v) => ({ ...v, notes: e.target.value }))}
              />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              C. Go / No-Go
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="decisionLabel">Decision</InputLabel>
                  <Select
                    labelId="decisionLabel"
                    label="Decision"
                    value={form.decision}
                    onChange={(e) => setForm((v) => ({ ...v, decision: e.target.value }))}
                  >
                    <MenuItem value="undecided">Undecided</MenuItem>
                    <MenuItem value="go">Go</MenuItem>
                    <MenuItem value="no-go">No-Go</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="confidenceLabel">Confidence</InputLabel>
                  <Select
                    labelId="confidenceLabel"
                    label="Confidence"
                    value={form.confidence}
                    onChange={(e) => setForm((v) => ({ ...v, confidence: e.target.value }))}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="med">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Main risk(s)"
                value={form.mainRisks}
                onChange={(e) => setForm((v) => ({ ...v, mainRisks: e.target.value }))}
              />
            </Stack>
          </CardContent>
        </Card>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => setForm(initial)}>
            Reset
          </Button>
          <Button variant="contained" onClick={onSave}>
            Save
          </Button>
        </Stack>
      </Container>
    </>
  );
}
